/**
 * Background Geolocation Service
 * 
 * This service manages background GPS tracking for automatic mileage logging.
 * Uses @capacitor-community/background-geolocation plugin.
 * 
 * Features:
 * - Auto-start on app launch (if enabled)
 * - Smart trip detection based on speed and movement patterns
 * - Battery-optimized tracking with distance filters
 * - Offline support with failed trip retry
 */

import { Capacitor } from '@capacitor/core';
import api from '../lib/api';

// Conditionally import - only works on native platforms
let BackgroundGeolocation = null;

// Storage keys
const STORAGE_KEYS = {
  AUTO_START_ENABLED: 'servio_tracking_autostart',
  SETUP_COMPLETE: 'servio_tracking_setup_complete',
  FAILED_TRIPS: 'servio_failed_trips',
  LAST_KNOWN_LOCATION: 'servio_last_location',
  TRACKING_STATS: 'servio_tracking_stats',
};

// Configuration for trip detection
const TRIP_CONFIG = {
  // Minimum distance (meters) between points to consider movement
  MIN_DISTANCE_THRESHOLD: 50,
  // Minimum speed (m/s) to consider driving (~5 mph = 2.2 m/s)
  MIN_DRIVING_SPEED: 2.2,
  // Maximum speed (m/s) to consider valid (~150 mph = 67 m/s) - filters GPS glitches
  MAX_VALID_SPEED: 67,
  // Time (ms) of no movement to end a trip
  TRIP_END_TIMEOUT: 5 * 60 * 1000, // 5 minutes
  // Minimum trip distance (meters) to save (~0.25 miles)
  MIN_TRIP_DISTANCE: 400,
  // Maximum accuracy to accept (meters)
  MAX_ACCURACY: 50,
  // Minimum trip duration (ms) to save
  MIN_TRIP_DURATION: 2 * 60 * 1000, // 2 minutes
  // Distance (meters) for location change to trigger movement detection
  SIGNIFICANT_DISTANCE: 100,
};

class GeolocationService {
  constructor() {
    this.isTracking = false;
    this.watcherId = null;
    this.currentTrip = null;
    this.lastLocation = null;
    this.tripEndTimer = null;
    this.listeners = new Set();
    this.isNativePlatform = Capacitor.isNativePlatform();
    this.autoStartEnabled = false;
    this.initialized = false;
    this.stats = {
      tripsToday: 0,
      milesToday: 0,
      lastTripEnd: null,
    };
  }

  /**
   * Initialize the plugin (must be called before using)
   * Optionally auto-starts tracking if enabled
   */
  async initialize() {
    if (!this.isNativePlatform) {
      console.log('GeolocationService: Not on native platform, skipping initialization');
      return false;
    }

    if (this.initialized) {
      return true;
    }

    try {
      // Dynamic import for native-only plugin
      const module = await import('@capacitor-community/background-geolocation');
      BackgroundGeolocation = module.BackgroundGeolocation;
      
      // Load saved settings
      this.loadSettings();
      
      // Load last known location
      this.loadLastLocation();
      
      this.initialized = true;
      console.log('GeolocationService: Plugin initialized successfully');
      
      // Auto-start if enabled and setup is complete
      const setupComplete = localStorage.getItem(STORAGE_KEYS.SETUP_COMPLETE) === 'true';
      if (this.autoStartEnabled && setupComplete) {
        console.log('GeolocationService: Auto-starting tracking...');
        setTimeout(() => this.startTracking(), 1000);
      }
      
      // Retry any failed trips
      this.retryFailedTrips();
      
      return true;
    } catch (error) {
      console.error('GeolocationService: Failed to initialize plugin', error);
      return false;
    }
  }

  /**
   * Load settings from storage
   */
  loadSettings() {
    try {
      this.autoStartEnabled = localStorage.getItem(STORAGE_KEYS.AUTO_START_ENABLED) === 'true';
      const statsJson = localStorage.getItem(STORAGE_KEYS.TRACKING_STATS);
      if (statsJson) {
        const savedStats = JSON.parse(statsJson);
        // Reset daily stats if it's a new day
        const today = new Date().toDateString();
        if (savedStats.date === today) {
          this.stats = savedStats;
        }
      }
    } catch (error) {
      console.error('GeolocationService: Error loading settings', error);
    }
  }

  /**
   * Save settings to storage
   */
  saveSettings() {
    try {
      localStorage.setItem(STORAGE_KEYS.AUTO_START_ENABLED, this.autoStartEnabled.toString());
      this.stats.date = new Date().toDateString();
      localStorage.setItem(STORAGE_KEYS.TRACKING_STATS, JSON.stringify(this.stats));
    } catch (error) {
      console.error('GeolocationService: Error saving settings', error);
    }
  }

  /**
   * Load last known location
   */
  loadLastLocation() {
    try {
      const locationJson = localStorage.getItem(STORAGE_KEYS.LAST_KNOWN_LOCATION);
      if (locationJson) {
        this.lastLocation = JSON.parse(locationJson);
      }
    } catch (error) {
      console.error('GeolocationService: Error loading last location', error);
    }
  }

  /**
   * Save last known location
   */
  saveLastLocation(location) {
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_KNOWN_LOCATION, JSON.stringify(location));
    } catch (error) {
      console.error('GeolocationService: Error saving last location', error);
    }
  }

  /**
   * Enable/disable auto-start on app launch
   */
  setAutoStart(enabled) {
    this.autoStartEnabled = enabled;
    this.saveSettings();
    this.notifyListeners('autostart_changed', { enabled });
    console.log(`GeolocationService: Auto-start ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Get auto-start status
   */
  getAutoStart() {
    return this.autoStartEnabled;
  }

  /**
   * Add a listener for trip events
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of an event
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('GeolocationService: Listener error', error);
      }
    });
  }

  /**
   * Request location permissions
   */
  async requestPermissions() {
    if (!this.isNativePlatform || !BackgroundGeolocation) {
      return { status: 'unavailable' };
    }

    try {
      // The plugin handles permission requests internally when starting
      return { status: 'granted' };
    } catch (error) {
      console.error('GeolocationService: Permission request failed', error);
      return { status: 'denied', error };
    }
  }

  /**
   * Start background location tracking
   */
  async startTracking() {
    if (!this.isNativePlatform) {
      console.warn('GeolocationService: Cannot start tracking - not on native platform');
      return { success: false, reason: 'not_native' };
    }

    if (!BackgroundGeolocation) {
      const initialized = await this.initialize();
      if (!initialized) {
        return { success: false, reason: 'plugin_not_available' };
      }
    }

    if (this.isTracking) {
      return { success: true, reason: 'already_tracking' };
    }

    try {
      this.watcherId = await BackgroundGeolocation.addWatcher(
        {
          // Android-specific options
          backgroundMessage: 'Servio is tracking your mileage',
          backgroundTitle: 'Mileage Tracking Active',
          requestPermissions: true,
          stale: false,
          // Higher accuracy for driving
          distanceFilter: TRIP_CONFIG.MIN_DISTANCE_THRESHOLD,
        },
        (location, error) => {
          if (error) {
            if (error.code === 'NOT_AUTHORIZED') {
              this.notifyListeners('permission_denied', error);
            } else {
              console.error('GeolocationService: Location error', error);
            }
            return;
          }

          if (location) {
            this.handleLocationUpdate(location);
          }
        }
      );

      this.isTracking = true;
      this.notifyListeners('tracking_started', null);
      console.log('GeolocationService: Tracking started with watcher ID:', this.watcherId);
      
      return { success: true };
    } catch (error) {
      console.error('GeolocationService: Failed to start tracking', error);
      return { success: false, reason: 'start_failed', error };
    }
  }

  /**
   * Stop background location tracking
   */
  async stopTracking() {
    if (!this.isTracking || !this.watcherId) {
      return { success: true };
    }

    try {
      await BackgroundGeolocation.removeWatcher({ id: this.watcherId });
      this.watcherId = null;
      this.isTracking = false;

      // End any current trip
      if (this.currentTrip) {
        await this.endTrip('manual_stop');
      }

      if (this.tripEndTimer) {
        clearTimeout(this.tripEndTimer);
        this.tripEndTimer = null;
      }

      this.notifyListeners('tracking_stopped', null);
      console.log('GeolocationService: Tracking stopped');
      
      return { success: true };
    } catch (error) {
      console.error('GeolocationService: Failed to stop tracking', error);
      return { success: false, error };
    }
  }

  /**
   * Handle incoming location updates
   */
  handleLocationUpdate(location) {
    const { latitude, longitude, accuracy, speed, time } = location;

    // Filter out inaccurate readings
    if (accuracy > TRIP_CONFIG.MAX_ACCURACY) {
      console.log('GeolocationService: Skipping inaccurate location', accuracy);
      return;
    }

    // Filter out impossible speeds (GPS glitches)
    if (speed !== null && speed > TRIP_CONFIG.MAX_VALID_SPEED) {
      console.log('GeolocationService: Skipping impossible speed', speed);
      return;
    }

    const timestamp = time || Date.now();
    const point = { latitude, longitude, accuracy, speed, timestamp };

    // Save last known location
    this.saveLastLocation(point);

    // Check if we're moving (driving)
    // Use both speed and distance from last point for better detection
    let isMoving = false;
    
    if (speed !== null && speed >= TRIP_CONFIG.MIN_DRIVING_SPEED) {
      isMoving = true;
    } else if (this.lastLocation) {
      // Check if we've moved significantly even if speed is not reported
      const distanceFromLast = this.calculateDistance(this.lastLocation, point);
      const timeDiff = timestamp - this.lastLocation.timestamp;
      if (timeDiff > 0) {
        const calculatedSpeed = (distanceFromLast / timeDiff) * 1000; // m/s
        if (calculatedSpeed >= TRIP_CONFIG.MIN_DRIVING_SPEED && distanceFromLast >= TRIP_CONFIG.SIGNIFICANT_DISTANCE) {
          isMoving = true;
        }
      }
    }

    if (isMoving) {
      // Reset trip end timer
      if (this.tripEndTimer) {
        clearTimeout(this.tripEndTimer);
        this.tripEndTimer = null;
      }

      if (!this.currentTrip) {
        this.startTrip(point);
      } else {
        this.addPointToTrip(point);
      }
    } else if (this.currentTrip) {
      // Not moving but have an active trip - start end timer
      // Also add this stationary point to capture the stop location
      this.addPointToTrip(point);
      
      if (!this.tripEndTimer) {
        this.tripEndTimer = setTimeout(() => {
          this.endTrip('timeout');
        }, TRIP_CONFIG.TRIP_END_TIMEOUT);
      }
    }

    this.lastLocation = point;
    this.notifyListeners('location_update', { point, isMoving, hasActiveTrip: !!this.currentTrip });
  }

  /**
   * Start a new trip
   */
  startTrip(point) {
    this.currentTrip = {
      id: `trip_${Date.now()}`,
      startTime: point.timestamp,
      startLocation: point,
      points: [point],
      totalDistance: 0,
    };

    this.notifyListeners('trip_started', {
      tripId: this.currentTrip.id,
      startLocation: point,
    });

    console.log('GeolocationService: Trip started', this.currentTrip.id);
  }

  /**
   * Add a point to the current trip
   */
  addPointToTrip(point) {
    if (!this.currentTrip) return;

    const lastPoint = this.currentTrip.points[this.currentTrip.points.length - 1];
    const distance = this.calculateDistance(lastPoint, point);

    // Only add if moved minimum distance
    if (distance >= TRIP_CONFIG.MIN_DISTANCE_THRESHOLD) {
      this.currentTrip.points.push(point);
      this.currentTrip.totalDistance += distance;

      this.notifyListeners('trip_progress', {
        tripId: this.currentTrip.id,
        point,
        totalDistance: this.currentTrip.totalDistance,
        pointCount: this.currentTrip.points.length,
      });
    }
  }

  /**
   * End the current trip
   */
  async endTrip(reason) {
    if (!this.currentTrip) return;

    const trip = this.currentTrip;
    this.currentTrip = null;

    if (this.tripEndTimer) {
      clearTimeout(this.tripEndTimer);
      this.tripEndTimer = null;
    }

    const endPoint = trip.points[trip.points.length - 1];
    const tripDuration = endPoint.timestamp - trip.startTime;

    // Check minimum distance
    if (trip.totalDistance < TRIP_CONFIG.MIN_TRIP_DISTANCE) {
      console.log('GeolocationService: Trip too short, discarding', trip.totalDistance, 'meters');
      this.notifyListeners('trip_discarded', {
        tripId: trip.id,
        reason: 'too_short',
        distance: trip.totalDistance,
      });
      return;
    }

    // Check minimum duration (to filter out GPS bounces)
    if (tripDuration < TRIP_CONFIG.MIN_TRIP_DURATION) {
      console.log('GeolocationService: Trip too brief, discarding', tripDuration, 'ms');
      this.notifyListeners('trip_discarded', {
        tripId: trip.id,
        reason: 'too_brief',
        duration: tripDuration,
      });
      return;
    }

    const distanceMiles = trip.totalDistance / 1609.34;
    
    const tripData = {
      id: trip.id,
      startTime: trip.startTime,
      endTime: endPoint.timestamp,
      duration: tripDuration,
      startLocation: trip.startLocation,
      endLocation: endPoint,
      points: trip.points,
      totalDistance: trip.totalDistance,
      distanceMiles: distanceMiles,
      reason,
    };

    // Update daily stats
    this.stats.tripsToday++;
    this.stats.milesToday += distanceMiles;
    this.stats.lastTripEnd = new Date().toISOString();
    this.saveSettings();

    this.notifyListeners('trip_ended', tripData);
    console.log('GeolocationService: Trip ended', tripData.id, tripData.distanceMiles.toFixed(2), 'miles');

    // Save to backend
    try {
      await this.saveTripToBackend(tripData);
    } catch (error) {
      console.error('GeolocationService: Failed to save trip', error);
      // Store locally for retry
      this.storeFailedTrip(tripData);
    }
  }

  /**
   * Calculate distance between two points (Haversine formula)
   */
  calculateDistance(point1, point2) {
    const R = 6371e3; // Earth's radius in meters
    const lat1 = (point1.latitude * Math.PI) / 180;
    const lat2 = (point2.latitude * Math.PI) / 180;
    const deltaLat = ((point2.latitude - point1.latitude) * Math.PI) / 180;
    const deltaLon = ((point2.longitude - point1.longitude) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Save completed trip to backend
   */
  async saveTripToBackend(tripData) {
    const startDate = new Date(tripData.startTime);
    const dateStr = startDate.toISOString().split('T')[0];

    // Reverse geocode start and end locations
    const [startAddress, endAddress] = await Promise.all([
      this.reverseGeocode(tripData.startLocation),
      this.reverseGeocode(tripData.endLocation),
    ]);

    const entry = {
      date: dateStr,
      from_address: startAddress || `${tripData.startLocation.latitude.toFixed(6)}, ${tripData.startLocation.longitude.toFixed(6)}`,
      to_address: endAddress || `${tripData.endLocation.latitude.toFixed(6)}, ${tripData.endLocation.longitude.toFixed(6)}`,
      miles: Math.round(tripData.distanceMiles * 100) / 100,
      is_business: null, // Let user classify later
      notes: `Auto-tracked trip (${tripData.points.length} points)`,
      // Include raw GPS data for map display
      gps_points: tripData.points.map(p => ({
        lat: p.latitude,
        lng: p.longitude,
        time: p.timestamp,
      })),
    };

    const response = await api.createAutoTrackedTrip(entry);
    
    this.notifyListeners('trip_saved', {
      tripId: tripData.id,
      entryId: response.data.id,
    });

    return response;
  }

  /**
   * Reverse geocode a location to an address
   */
  async reverseGeocode(location) {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${location.longitude},${location.latitude}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}&limit=1`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        return data.features[0].place_name;
      }
    } catch (error) {
      console.error('GeolocationService: Reverse geocode failed', error);
    }
    return null;
  }

  /**
   * Store failed trip locally for retry
   */
  storeFailedTrip(tripData) {
    try {
      const failedTrips = JSON.parse(localStorage.getItem('servio_failed_trips') || '[]');
      failedTrips.push(tripData);
      localStorage.setItem('servio_failed_trips', JSON.stringify(failedTrips));
      console.log('GeolocationService: Stored failed trip for retry');
    } catch (error) {
      console.error('GeolocationService: Failed to store trip locally', error);
    }
  }

  /**
   * Retry saving failed trips
   */
  async retryFailedTrips() {
    const failedTrips = JSON.parse(localStorage.getItem('servio_failed_trips') || '[]');
    if (failedTrips.length === 0) return { retried: 0, failed: 0 };

    let retried = 0;
    let failed = 0;
    const stillFailed = [];

    for (const tripData of failedTrips) {
      try {
        await this.saveTripToBackend(tripData);
        retried++;
      } catch (error) {
        failed++;
        stillFailed.push(tripData);
      }
    }

    localStorage.setItem('servio_failed_trips', JSON.stringify(stillFailed));
    return { retried, failed };
  }

  /**
   * Get tracking status
   */
  getStatus() {
    return {
      isNativePlatform: this.isNativePlatform,
      isInitialized: this.initialized,
      isTracking: this.isTracking,
      autoStartEnabled: this.autoStartEnabled,
      hasActiveTrip: !!this.currentTrip,
      lastLocation: this.lastLocation,
      currentTrip: this.currentTrip ? {
        id: this.currentTrip.id,
        startTime: this.currentTrip.startTime,
        pointCount: this.currentTrip.points.length,
        totalDistance: this.currentTrip.totalDistance,
        distanceMiles: this.currentTrip.totalDistance / 1609.34,
      } : null,
      stats: this.stats,
    };
  }

  /**
   * Get daily statistics
   */
  getDailyStats() {
    return {
      tripsToday: this.stats.tripsToday,
      milesToday: this.stats.milesToday,
      lastTripEnd: this.stats.lastTripEnd,
    };
  }

  /**
   * Get count of failed trips pending retry
   */
  getFailedTripsCount() {
    try {
      const failedTrips = JSON.parse(localStorage.getItem(STORAGE_KEYS.FAILED_TRIPS) || '[]');
      return failedTrips.length;
    } catch {
      return 0;
    }
  }

  /**
   * Open app settings (for permission management)
   */
  async openSettings() {
    if (!this.isNativePlatform || !BackgroundGeolocation) {
      return;
    }

    try {
      await BackgroundGeolocation.openSettings();
    } catch (error) {
      console.error('GeolocationService: Failed to open settings', error);
    }
  }
}

// Export singleton instance
export const geolocationService = new GeolocationService();
export default geolocationService;
